import { backboneService } from '@/services/backbone.js';
import { assetsClient } from '@/clients/assets-client.js';
import { assetTypesCache } from '@/services/asset-types-cache.js';
import { HatElement } from '@/components/hat-element.js';

class HatAssetProfile extends HatElement {
    private _data: any = null;
    private _assetTypeLabel: string | null = null;
    private _locationId: string | null = null;
    private _assetId: string | null = null;
    private _outsideClickHandler: ((e: MouseEvent) => void) | null = null;

    // ─── Lifecycle ────────────────────────────────────────────────────────────

    static get observedAttributes() {
        return ['location-id', 'asset-id'];
    }

    connectedCallback() {
        super.connectedCallback();
        this._locationId = this.getAttribute('location-id');
        this._assetId = this.getAttribute('asset-id');
        if (this._locationId && this._assetId) this.loadData();
    }

    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
        if (oldValue === newValue) return;
        if (name === 'location-id') this._locationId = newValue;
        if (name === 'asset-id') this._assetId = newValue;
        if (this.isConnected && this._locationId && this._assetId) this.loadData();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._outsideClickHandler) {
            document.removeEventListener('click', this._outsideClickHandler);
        }
    }

    // ─── Data ─────────────────────────────────────────────────────────────────

    async loadData() {
        this._data = await assetsClient.getAsset(this._locationId!, this._assetId!);
        if (this._data.assetType) {
            const type = await assetTypesCache.getByCode(this._data.assetType);
            this._assetTypeLabel = type?.description ?? this._data.assetType;
        }
        this._render();
    }

    // ─── Rendering ────────────────────────────────────────────────────────────

    _render() {
        if (!this._data) return;

        const { name, assetType, displayImage, images, properties, userDefinedProperties } = this._data;

        const thumbnailDocId = displayImage?.thumbnailDocId
            ?? images?.find((img: any) => img.id === this._data.displayImageId)?.thumbnailDocId;

        this.root.innerHTML = `
            <style>
                .container {
                    max-width: 1000px;
                    margin: 0 auto;
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                }

                .top-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .menu { position: relative; }

                .menu-btn {
                    padding: 6px 12px;
                    border-radius: 8px;
                    border: 1px solid #ddd;
                    background: #f7f7f7;
                    cursor: pointer;
                    font-size: 16px;
                }

                .menu-btn:hover { background: #ececec; }

                .dropdown {
                    position: absolute;
                    right: 0;
                    top: 42px;
                    background: white;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    display: none;
                    flex-direction: column;
                    min-width: 140px;
                    overflow: hidden;
                    z-index: 10;
                }

                .dropdown button {
                    padding: 8px 12px;
                    border: none;
                    background: none;
                    text-align: left;
                    cursor: pointer;
                }

                .dropdown button:hover { background: #f2f2f2; }
                .menu.open .dropdown { display: flex; }

                .profile {
                    background: #fff;
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 6px 20px rgba(0,0,0,0.08);
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                }

                .top {
                    display: flex;
                    gap: 20px;
                    align-items: flex-start;
                }

                .hero-image {
                    width: min(200px, 100%);
                    border-radius: 16px;
                    overflow: hidden;
                    flex-shrink: 0;
                }

                .hero-image hat-image { width: 100%; height: auto; display: block; }

                .info { flex: 1; min-width: 0; }

                .name {
                    font-size: 22px;
                    font-weight: 600;
                    word-break: break-word;
                }

                .asset-type {
                    font-size: 14px;
                    color: #666;
                    margin-top: 4px;
                }

                .section {
                    background: #fff;
                    border-radius: 16px;
                    padding: 20px;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
                }

                .section h3 {
                    margin: 0 0 12px;
                    font-size: 15px;
                    font-weight: 600;
                }

                .prop-row {
                    display: flex;
                    gap: 12px;
                    padding: 8px 0;
                    border-bottom: 1px solid #f0f0f0;
                    font-size: 14px;
                }

                .prop-row:last-child { border-bottom: none; }

                .prop-label {
                    color: #888;
                    min-width: 140px;
                    flex-shrink: 0;
                    text-transform: capitalize;
                }

                .prop-value { color: #222; }

                .modal {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.92);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    padding: 20px;
                    box-sizing: border-box;
                }

                .modal hat-image {
                    width: auto;
                    height: auto;
                    max-width: 90vw;
                    max-height: 90vh;
                    border-radius: 12px;
                }

                @media (max-width: 600px) {
                    .top { flex-direction: column; }
                    .hero-image { width: 100%; }
                }
            </style>

            <div class="container">
                <div class="top-bar">
                    <hat-nav-button href="/app/locations/${this._locationId}">Back</hat-nav-button>
                    <div class="menu" hat-entitlement="feature.asset.edit feature.asset.delete">
                        <button class="menu-btn">⋮</button>
                        <div class="dropdown">
                            <button data-action="edit" hat-entitlement="feature.asset.edit">Edit</button>
                            <button data-action="delete" hat-entitlement="feature.asset.delete">Delete</button>
                        </div>
                    </div>
                </div>

                <div class="profile">
                    <div class="top">
                        ${thumbnailDocId ? `
                            <div class="hero-image">
                                <hat-image type="fullsize" document-id="${thumbnailDocId}"></hat-image>
                            </div>
                        ` : ''}
                        <div class="info">
                            <div class="name">${name ?? 'Unnamed asset'}</div>
                            <div class="asset-type">${this._assetTypeLabel ?? assetType ?? ''}</div>
                        </div>
                    </div>

                    <hat-location-gallery mode="view"></hat-location-gallery>
                </div>

                ${properties?.length ? `
                    <div class="section">
                        <h3>Properties</h3>
                        ${properties.map((p: any) => `
                            <div class="prop-row">
                                <span class="prop-label">${p.code?.replace(/_/g, ' ').toLowerCase() ?? ''}</span>
                                <span class="prop-value">${p.value ?? ''}</span>
                            </div>
                        `).join('')}
                    </div>
                ` : ''}

                ${userDefinedProperties?.length ? `
                    <div class="section">
                        <h3>Additional Properties</h3>
                        ${userDefinedProperties.map((p: any) => `
                            <div class="prop-row">
                                <span class="prop-label">${p.name ?? ''}</span>
                                <span class="prop-value">${p.value ?? ''}</span>
                            </div>
                        `).join('')}
                    </div>
                ` : ''}

                <hat-maintenance-list
                    location-id="${this._locationId}"
                    asset-id="${this._assetId}"
                    asset-created-at="${this._data.createdTS ?? ''}">
                </hat-maintenance-list>
            </div>
        `;

        const gallery = this.root.querySelector('hat-location-gallery')!;
        gallery.setImages(images ?? []);
        if (this._data.displayImage?.id) {
            gallery.setAttribute('primary-image-id', this._data.displayImage.id);
        }

        this._setupEvents();
        this._applyEntitlements();
    }

    // ─── Events ───────────────────────────────────────────────────────────────

    _setupEvents() {
        const menu   = this.root.querySelector<HTMLElement>('.menu')!;
        const button = menu.querySelector<HTMLElement>('.menu-btn')!;
        const gallery = this.root.querySelector('hat-location-gallery')!;

        button.onclick = (e) => {
            e.stopPropagation();
            menu.classList.toggle('open');
        };

        menu.querySelector<HTMLElement>('[data-action="edit"]')!.onclick = () => {
            menu.classList.remove('open');
            backboneService.navigateTo(`/app/locations/${this._locationId}/assets/${this._assetId}/edit`);
        };

        menu.querySelector<HTMLElement>('[data-action="delete"]')!.onclick = async () => {
            menu.classList.remove('open');
            await assetsClient.deleteAsset(this._locationId!, this._assetId!);
            backboneService.navigateTo(`/app/locations/${this._locationId}`);
        };

        gallery.addEventListener('image-expanded', (e: Event) => {
            this._openImage((e as CustomEvent).detail);
        });

        this._outsideClickHandler = (e) => {
            if (!e.composedPath().includes(menu)) menu.classList.remove('open');
        };
        document.addEventListener('click', this._outsideClickHandler);
    }

    // ─── Modal ────────────────────────────────────────────────────────────────

    _openImage({ documentId }: { documentId?: string }) {
        const modal = document.createElement('div');
        modal.className = 'modal';
        const img = document.createElement('hat-image');
        img.setAttribute('type', 'expanded');
        img.setAttribute('document-id', documentId ?? '');
        modal.appendChild(img);
        modal.onclick = () => modal.remove();
        this.root.appendChild(modal);
    }
}

customElements.define('hat-asset-profile', HatAssetProfile);